import { SessionStore } from '@backend/auth'
import { ApprovedOpenReservationFilter, Reservation, ReservationSource, Table, TableCombination } from '@backend/domain'
import { ReservationRepository, TableCombinationRepository, TableRepository } from '@backend/repository'
import {
	BatchAvailabilityLookupRequest,
	BatchAvailabilityLookupResponse,
	Booking,
	BookingFailure_Cause,
	BookingStatus,
	CreateBookingRequest,
	CreateBookingResponse,
	GetBookingStatusRequest,
	GetBookingStatusResponse,
	SlotTime,
	SlotTimeAvailability,
	UpdateBookingRequest,
	UpdateBookingResponse,
} from '@core/google-reservations/types/booking-server'
import { ReservationState } from '@core/types'
import { BadRequestException, Injectable, Logger } from '@nestjs/common'
import { addSeconds, fromUnixTime } from 'date-fns'
import { In } from 'typeorm'
import { Transactional } from 'typeorm-transactional'
import { ReservationUtilsService, TableAssignmentService } from '../../../shared'
import { getOpenTableCount } from '../../../utils'

@Injectable()
export class BookingServerService {
	private readonly logger = new Logger(BookingServerService.name)

	constructor(
		private readonly _tableRepository: TableRepository,
		private readonly _reservationRepository: ReservationRepository,
		private readonly _tableAssignmentService: TableAssignmentService,
		private readonly _reservationUtilsService: ReservationUtilsService,
		private readonly _tableCombinationRepository: TableCombinationRepository,
	) {}

	async batchAvailabilityLookup(request: BatchAvailabilityLookupRequest): Promise<BatchAvailabilityLookupResponse> {
		const { merchant_id: restaurantId, slot_time } = request

		if (!restaurantId) {
			this.logger.error('merchant_id not provided')
			throw new BadRequestException()
		}

		if (!slot_time) {
			this.logger.error({ message: 'slot_time not provided', restaurantId })
			throw new BadRequestException()
		}

		SessionStore.tenantId = restaurantId

		const tables = await this._tableRepository.find()
		// Tenant-scoped via SessionStore (same as tables); needed so large parties that only
		// fit a table combination are reported available, matching the availability feed.
		const combinations = await this._tableCombinationRepository.find({ relations: { tables: true } })

		const slotTimeAvailability: SlotTimeAvailability[] = []

		for (const slot of slot_time) {
			if (!slot.start_sec || !slot.duration_sec) {
				this.logger.error({ message: 'start_sec or duration_sec not provided', restaurantId })
				// Google expects an answer for every requested slot
				slotTimeAvailability.push({ slot_time: slot, available: false })
				continue
			}

			const available = await this.isSlotAvailable(slot, tables, combinations)
			slotTimeAvailability.push({ slot_time: slot, available })
		}

		return { slot_time_availability: slotTimeAvailability }
	}

	@Transactional()
	async createBooking(request: CreateBookingRequest): Promise<CreateBookingResponse> {
		const { slot, user_information, additional_request, idempotency_token } = request

		if (!slot) {
			this.logger.error('slot is not provided')
			return {
				booking_failure: {
					cause: BookingFailure_Cause.CAUSE_UNSPECIFIED,
					description: 'slot is not provided',
				},
			}
		}

		const { merchant_id: restaurantId, start_sec, duration_sec, resources } = slot

		if (!restaurantId) {
			this.logger.error('restaurantId is not provided')
			return {
				booking_failure: {
					cause: BookingFailure_Cause.CAUSE_UNSPECIFIED,
					description: 'merchant_id is not provided',
				},
			}
		}

		SessionStore.tenantId = restaurantId

		if (!start_sec || !duration_sec) {
			this.logger.error({ message: 'One of start_sec or duration_sec is not provided', restaurantId })
			return {
				booking_failure: {
					cause: BookingFailure_Cause.CAUSE_UNSPECIFIED,
					description: 'One of start_sec or duration_sec is not provided',
				},
			}
		}

		const startDate = fromUnixTime(Number(start_sec))
		const endDate = addSeconds(startDate, Number(duration_sec))

		if (!resources?.party_size) {
			this.logger.error('party_size is not provided')

			return {
				booking_failure: {
					cause: BookingFailure_Cause.CAUSE_UNSPECIFIED,
					description: 'party_size is not provided',
				},
			}
		}

		// Serialize per tenant so two concurrent requests cannot both grab the last open slot
		await this._reservationRepository.acquireBookingLock(restaurantId)

		if (idempotency_token) {
			// Tenant scoping is injected by the repository, but keep it explicit as defense in depth
			const existing = await this._reservationRepository.findOne({
				where: { tenantId: restaurantId, idempotencyToken: idempotency_token },
			})

			// A booking already created for this idempotency token must be returned as-is
			if (existing) {
				this.logger.log({
					message: 'Returning existing reservation for repeated CreateBooking request',
					id: existing.id,
					tenantId: existing.tenantId,
				})

				return {
					booking: {
						booking_id: existing.id,
						slot,
						status: existing.state === ReservationState.rejected ? BookingStatus.CANCELED : BookingStatus.CONFIRMED,
						user_information,
					},
				}
			}
		}

		const reservation = {
			noOfPersons: resources.party_size,
			startDate,
			endDate,
			customerData: {
				name: `${user_information?.given_name} ${user_information?.family_name ? ' ' + user_information?.family_name : ''}`,
				email: user_information?.email,
				phoneNumber: user_information?.telephone,
			},
			// We don't support async approvals via Google reservations yet so reservation is always approved
			state: ReservationState.approved,
			comment: additional_request,
			source: ReservationSource.google,
			idempotencyToken: idempotency_token,
		} as Reservation

		const { tables, combination } = await this._tableAssignmentService.assignTable(reservation)

		// Because we don't support async approvals yet, a table must be available
		if (tables.length === 0) {
			return {
				booking_failure: { cause: BookingFailure_Cause.SLOT_UNAVAILABLE },
			}
		}

		reservation.tables = tables
		reservation.tableCombination = combination

		const entity = await this._reservationRepository.save(reservation)

		this._reservationUtilsService.notifyNewReservation(entity)

		this.logger.log({
			message: 'Created reservation via Google Booking Server',
			id: entity.id,
			tenantId: entity.tenantId,
			noOfPersons: entity.noOfPersons,
		})

		const booking: Booking = {
			booking_id: entity.id,
			slot,
			status: BookingStatus.CONFIRMED,
			user_information,
		}

		return { booking }
	}

	async updateBooking(request: UpdateBookingRequest): Promise<UpdateBookingResponse> {
		// TODO: Use idempotency token to validate duplicate request
		const { booking } = request

		if (!booking) {
			this.logger.error('booking is not provided')
			return {
				booking_failure: {
					cause: BookingFailure_Cause.CAUSE_UNSPECIFIED,
					description: 'booking is not provided',
				},
			}
		}

		const { booking_id, slot, status } = booking

		const reservation = await this._reservationRepository.findOne({
			where: { id: booking_id },
		})

		if (!reservation) {
			this.logger.error({ message: 'Reservation not found', reservationId: booking_id })
			return {
				booking_failure: {
					cause: BookingFailure_Cause.CAUSE_UNSPECIFIED,
					description: 'booking not found',
				},
			}
		}

		SessionStore.tenantId = reservation.tenantId!

		// If status was changed
		// We only handle CANCELED status for now
		if (status) {
			if (status === BookingStatus.CANCELED) {
				await this._reservationRepository.update({ id: reservation.id }, { state: ReservationState.rejected })
				return {
					booking: {
						booking_id: reservation.id,
						status: BookingStatus.CANCELED,
					},
				}
			}

			this.logger.error({ message: 'Unhandled booking status', reservationId: reservation.id, status })
			return {
				booking_failure: {
					cause: BookingFailure_Cause.CAUSE_UNSPECIFIED,
					description: `Unsupported booking status update: ${status}`,
				},
			}
		}

		if (slot) {
			const { start_sec, duration_sec, resources } = slot

			if (start_sec || duration_sec) {
				const startDate = fromUnixTime(Number(start_sec))
				const endDate = addSeconds(startDate, Number(duration_sec))

				reservation.startDate = startDate
				reservation.endDate = endDate
			}

			if (resources?.party_size) {
				reservation.noOfPersons = resources.party_size
			}

			const { tables, combination } = await this._tableAssignmentService.assignTable(reservation)

			// Because we don't support async approvals, a table must be available
			if (tables.length === 0) {
				return {
					booking_failure: {
						cause: BookingFailure_Cause.SLOT_UNAVAILABLE,
					},
				}
			}

			reservation.tables = tables
			reservation.tableCombination = combination

			await this._reservationRepository.save(reservation)

			// TODO: Send reservation update event

			this.logger.log({
				message: 'Updated Google Booking',
				reservationId: reservation.id,
				tenantId: reservation.tenantId,
				noOfPersons: reservation.noOfPersons,
			})

			return {
				booking: {
					booking_id: reservation.id,
					slot,
				},
			}
		}

		// If nothing to update, just return the booking
		return {
			booking: {
				booking_id: reservation.id,
			},
		}
	}

	async getBookingStatus(request: GetBookingStatusRequest): Promise<GetBookingStatusResponse> {
		const { booking_id } = request

		if (!booking_id) {
			this.logger.error('booking_id is not provided')
			return {
				booking_id,
				booking_status: BookingStatus.BOOKING_STATUS_UNSPECIFIED,
			}
		}

		const reservation = await this._reservationRepository.findOne({
			where: { id: booking_id },
		})

		if (!reservation) {
			this.logger.error({ message: 'Reservation not found', reservationId: booking_id })
			return {
				booking_id,
				booking_status: BookingStatus.BOOKING_STATUS_UNSPECIFIED,
			}
		}

		return {
			booking_id: reservation.id,
			booking_status:
				reservation.state === ReservationState.rejected ? BookingStatus.CANCELED : BookingStatus.CONFIRMED,
		}
	}

	// TODO: Refactor to batch check slot availability
	private async isSlotAvailable(slot: SlotTime, tables: Table[], combinations: TableCombination[]): Promise<boolean> {
		const startDate = fromUnixTime(Number(slot.start_sec))
		const endDate = addSeconds(startDate, Number(slot.duration_sec))
		const partySize = slot.resource_ids?.party_size

		// Fetch Overlapping approved reservations with their assigned tables
		const reservations = await this._reservationRepository.findByInterval(startDate, endDate, {
			where: {
				state: In(ApprovedOpenReservationFilter),
			},
			relations: { tables: true },
		})

		// Same calculation as the availability feed (spots_open), so feed and lookup always agree
		return getOpenTableCount(tables, reservations, partySize, combinations) > 0
	}
}
